/**
 * How one roster cell is drawn (BON-534).
 *
 * Most cells are whatever the column model renders. Two are drawn here instead:
 * `region` carries the row's own controls, and `tickets` needs a component
 * rather than a value. Keeping that decision in one place is what lets the
 * header, the body and the grid track list stay derived from one array.
 */
import type { ReactNode, CSSProperties } from 'react';

import { FillBar } from '../../primitives/FillBar';

import type { OverviewRegionRow } from './overview-types';
import { fillPercent, type RosterColumn } from './roster-columns';

export const ROSTER_CELL: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  padding: '0 8px',
  // The roster is a grid rather than a `<table>`, so it misses `.nb-tbl`'s base
  // size and inherits the page's instead — which made the director read LARGER
  // than the region name beside it. 13px is what the design's table uses.
  fontSize: 13,
};

export function rosterCellContent(
  column: RosterColumn,
  row: OverviewRegionRow,
  controls: ReactNode,
): ReactNode {
  if (column.id === 'region') return controls;
  if (column.id === 'tickets') {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <FillBar
          sold={row.ticketsSold}
          cap={row.ticketCapacity}
          width={56}
          ariaLabel={`${row.ticketsSold} of ${row.ticketCapacity} tickets sold`}
        />
        <span className="nb-mono nb-tabular" style={{ fontSize: 10.5, color: 'var(--nb-muted)' }}>
          {fillPercent(row.ticketsSold, row.ticketCapacity)}%
        </span>
      </span>
    );
  }
  return column.render(row);
}
